/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Grid, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import type { TFunction } from 'i18next';
import { cloneDeep } from 'lodash';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormRadioGroup,
  ReactHookFormSelect,
  ReactHookFormNumberField,
  isValidMax,
  isInteger,
  isValidMin,
  isEmpty,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import { usePrevious } from '@opengeoweb/shared';
import type { ConfigurableFormFieldProps, ProductConfig } from '../../../types';
import { LevelUnits } from '../../../types';
import { styles } from '../ProductForm.styles';
import {
  getActiveFIRArea,
  getAllowedUnits,
  getMaxLevelValue,
  getMinLevelValue,
  isLevelLower,
  triggerValidations,
} from '../utils';
import ProductFormFieldLayout from './ProductFormFieldLayout';
import { RadioButtonAndLabel } from './RadioButtonAndLabel';
import { useShortTestHelpers } from '../../../hooks/useShortTestHelpers';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';

export const DEFAULT_ROUNDING_LEVELS_FL = 5;
export const DEFAULT_ROUNDING_LEVELS_FT = 500;
export const DEFAULT_ROUNDING_LEVELS_M = 1;

// Helper to unfreeze an unintended immutable level field.
const ensureLevelObjectIsEditable = (
  parentPath: 'level' | 'lowerLevel',
  getValues: (name: string) => unknown,
  setValue: (
    name: string,
    value: unknown,
    options?: { shouldDirty?: boolean },
  ) => void,
): void => {
  const parentValue = getValues(parentPath);
  if (!parentValue || typeof parentValue !== 'object') {
    return;
  }
  if (!Object.isFrozen(parentValue)) {
    return;
  }

  setValue(parentPath, cloneDeep(parentValue), { shouldDirty: false });
};

export const getLevelInvalidStepsForFLUnitMessage = (
  t: TFunction,
  value = DEFAULT_ROUNDING_LEVELS_FL,
): string => t('levels-get-level-invalid-steps-for-fl-unit-message', { value });

export const getLevelInvalidStepsForFTUnitMessage = (
  t: TFunction,
  value = DEFAULT_ROUNDING_LEVELS_FT,
): string => t('levels-get-level-invalid-steps-for-ft-unit-message', { value });

export const getLevelInvalidStepsForMUnitMessage = (
  t: TFunction,
  value = DEFAULT_ROUNDING_LEVELS_M,
): string => t('levels-get-level-invalid-steps-for-m-unit-message', { value });

export const getInvalidUnitMessage = (t: TFunction): string =>
  t('levels-invalid-unit-message');

export const validateLevels = (
  t: TFunction,
  value: string,
  unit: string,
  productConfig: ProductConfig,
  selectedFIR: string,
): boolean | string => {
  if (isEmpty(value)) {
    return true;
  }

  const {
    /* eslint-disable @typescript-eslint/naming-convention */
    level_rounding_FL = DEFAULT_ROUNDING_LEVELS_FL,
    level_rounding_FT = DEFAULT_ROUNDING_LEVELS_FT,
    level_rounding_M = DEFAULT_ROUNDING_LEVELS_M,
    /* eslint-enable @typescript-eslint/naming-convention */
  } = getActiveFIRArea(selectedFIR, productConfig);

  // Parse to integer to check for steps
  const intLevel = parseInt(value, 10);
  if (LevelUnits[unit as keyof typeof LevelUnits] === LevelUnits.FT) {
    return (
      intLevel % level_rounding_FT === 0 ||
      getLevelInvalidStepsForFTUnitMessage(t, level_rounding_FT)
    );
  }
  if (LevelUnits[unit as keyof typeof LevelUnits] === LevelUnits.FL) {
    return (
      intLevel % level_rounding_FL === 0 ||
      getLevelInvalidStepsForFLUnitMessage(t, level_rounding_FL)
    );
  }
  if (LevelUnits[unit as keyof typeof LevelUnits] === LevelUnits.M) {
    return (
      intLevel % level_rounding_M === 0 ||
      getLevelInvalidStepsForMUnitMessage(t, level_rounding_M)
    );
  }
  return getInvalidUnitMessage(t);
};

export const validateLevelUnitCombinations = (
  t: TFunction,
  levelUnit: string,
  lowerLevelUnit: string,
): boolean | string => {
  // If different units and lower level not SFC, only FL is allowed as upper level
  if (
    lowerLevelUnit !== 'SFC' &&
    levelUnit !== lowerLevelUnit &&
    LevelUnits[levelUnit as keyof typeof LevelUnits] !== LevelUnits.FL
  ) {
    return t('levels-invalid-level-unit-combination-message');
  }

  return true;
};

export const tsPhenomena = new Set([
  'EMBD_TS',
  'EMBD_TSGR',
  'FRQ_TS',
  'FRQ_TSGR',
  'OBSC_TS',
  'OBSC_TSGR',
  'SQL_TS',
  'SQL_TSGR',
  'ISOL_TS',
  'ISOL_TSGR',
  'OCNL_TS',
  'OCNL_TSGR',
]);

export const isTSPhenomenon = (phenomenon?: string): boolean =>
  !!phenomenon && tsPhenomena.has(phenomenon);

const Levels: React.FC<ConfigurableFormFieldProps> = ({
  isDisabled,
  isReadOnly,
  onChange = (): void => {},
  productConfig,
  productType,
}: ConfigurableFormFieldProps) => {
  const { t } = useSigmetAirmetTranslation();
  const { watch, setValue, trigger, getValues, clearErrors, unregister } =
    useFormContext();
  const { isRequired: isRequiredField } = useDraftFormHelpers();
  const { isShortTest } = useShortTestHelpers();
  const ensureEditable = React.useCallback(
    (parentPath: 'level' | 'lowerLevel'): void => {
      ensureLevelObjectIsEditable(parentPath, getValues, setValue);
    },
    [getValues, setValue],
  );
  const isRequired = (value: string): string | boolean =>
    isShortTest() || isRequiredField(value);

  const level = watch('level.value');
  const levelInfoMode = watch('levelInfoMode');
  const isRadioactiveCloud = watch('phenomenon') === 'RDOACT_CLD';
  const fieldsDisabled = isDisabled || isShortTest() || isRadioactiveCloud;
  const phenomenon = watch('phenomenon');

  const isTS = isTSPhenomenon(phenomenon);
  const isSigmetTS = productType === 'sigmet' && isTS;
  const prevPhenomenon = usePrevious(phenomenon);

  React.useEffect(() => {
    if (productType !== 'sigmet') {
      return;
    }

    if (isSigmetTS) {
      if (levelInfoMode === 'AT') {
        setValue('levelInfoMode', 'TOPS', { shouldDirty: true });
      } else if (levelInfoMode === 'ABV') {
        setValue('levelInfoMode', 'TOPS_ABV', { shouldDirty: true });
      }
    }

    if (!isTS && tsPhenomena.has(prevPhenomenon || '')) {
      if (levelInfoMode === 'TOPS') {
        setValue('levelInfoMode', 'AT', { shouldDirty: true });
      } else if (levelInfoMode === 'TOPS_ABV') {
        setValue('levelInfoMode', 'ABV', { shouldDirty: true });
      }
    }
  }, [isSigmetTS, isTS, levelInfoMode, setValue, prevPhenomenon, productType]);

  const getFIRValue = (): string =>
    getValues('locationIndicatorATSR') as string;

  // Get allowed level units based on selected FIR - if no FIR selected, allow all default units
  const allowedLevelUnitsForFir = getAllowedUnits(
    getFIRValue(),
    productConfig,
    'level_unit',
    LevelUnits,
  );

  const radioactiveCloudLevel = getMaxLevelValue(
    LevelUnits.FL,
    getFIRValue(),
    productConfig,
  );

  React.useEffect(() => {
    if (levelInfoMode !== 'BETW') {
      unregister('lowerLevel');
    }
    if (isRadioactiveCloud) {
      setValue('level.unit', 'FL');
      setValue('level.value', radioactiveCloudLevel);
      triggerValidations(['level.value'], getValues, trigger);
    }
  }, [
    getValues,
    isRadioactiveCloud,
    levelInfoMode,
    radioactiveCloudLevel,
    setValue,
    trigger,
    unregister,
  ]);

  return (
    <ProductFormFieldLayout
      sx={{
        ...styles.containerItem,
        '.MuiFormHelperText-root.Mui-error': {
          marginBottom: '-16px',
        },
      }}
      title={t('levels')}
    >
      <ReactHookFormRadioGroup
        disabled={fieldsDisabled}
        isReadOnly={isReadOnly}
        name="levelInfoMode"
        onChange={(): void => {
          const currentLevel = getValues('level');
          if (currentLevel && typeof currentLevel === 'object') {
            if (Object.isFrozen(currentLevel)) {
              setValue('level', cloneDeep(currentLevel), {
                shouldDirty: false,
              });
            }
          }
          const currentLowerLevel = getValues('lowerLevel');
          if (currentLowerLevel && typeof currentLowerLevel === 'object') {
            if (Object.isFrozen(currentLowerLevel)) {
              setValue('lowerLevel', cloneDeep(currentLowerLevel), {
                shouldDirty: false,
              });
            }
          }

          // Reset default value for the level value to '' so when it rerenders it uses this empty default value
          setValue('level.unit', 'FL');
          setValue('level.value', null);

          // Clear out errors for the levels
          clearErrors(['level', 'lowerLevel']);
        }}
        rules={{ validate: { isRequired } }}
      >
        {/** AT */}
        <RadioButtonAndLabel
          checked={levelInfoMode === 'AT' || levelInfoMode === 'TOPS'}
          data-testid="levels-AT"
          disabled={fieldsDisabled}
          label={t('at')}
          value="AT"
        />
        {(levelInfoMode === 'AT' || levelInfoMode === 'TOPS') &&
        !isRadioactiveCloud ? (
          <Grid
            alignItems="flex-start"
            container
            justifyItems="flex-end"
            spacing={2}
          >
            <Grid
              size={{
                xs: 12,
                md: 3,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={levelInfoMode === 'TOPS'}
                    color="secondary"
                    disabled={isDisabled || isSigmetTS}
                    onChange={(): void => {
                      if (isSigmetTS) {
                        return;
                      }

                      setValue(
                        'levelInfoMode',
                        levelInfoMode === 'AT' ? 'TOPS' : 'AT',
                        { shouldDirty: true },
                      );
                    }}
                  />
                }
                data-testid="levels-TOPS"
                label={t('levels-tops-label')}
              />
            </Grid>
            <Grid
              size={{
                xs: 4,
                md: 3,
              }}
            >
              <ReactHookFormSelect
                defaultValue={'FL' as LevelUnits}
                disabled={isDisabled}
                isReadOnly={isReadOnly}
                label={t('unit-label')}
                name="level.unit"
                onBeforeChange={(): void => {
                  ensureEditable('level');
                }}
                onChange={(): void => {
                  triggerValidations(['level.value'], getValues, trigger);
                  onChange();
                }}
                rules={{ validate: { isRequired } }}
                size="small"
                sx={styles.unit}
              >
                {Object.keys(allowedLevelUnitsForFir).map((key) => (
                  <MenuItem key={key} value={key}>
                    {allowedLevelUnitsForFir[key]}
                  </MenuItem>
                ))}
              </ReactHookFormSelect>
            </Grid>
            <Grid
              size={{
                xs: 8,
                md: 6,
              }}
            >
              <ReactHookFormNumberField
                autoFocus
                data-testid="level.value"
                disabled={isDisabled}
                isReadOnly={isReadOnly}
                label={t('level')}
                name="level.value"
                onBeforeChange={(): void => {
                  ensureEditable('level');
                }}
                rules={{
                  validate: {
                    isRequired,
                    isInteger,
                    max: (value): boolean | string =>
                      // The max level depends on the unit
                      isValidMax(
                        value,
                        getMaxLevelValue(
                          getValues('level.unit'),
                          getFIRValue(),
                          productConfig,
                        ),
                      ) ||
                      t('levels-the-maximum-level', {
                        levelUnit:
                          LevelUnits[
                            getValues('level.unit') as keyof typeof LevelUnits
                          ],
                        maxLevelValue: getMaxLevelValue(
                          getValues('level.unit'),
                          getFIRValue(),
                          productConfig,
                        ),
                      }),
                    min: (value): boolean | string =>
                      // The min level depends on the unit
                      isValidMin(
                        value,
                        getMinLevelValue(
                          getValues('level.unit'),
                          getFIRValue(),
                          productConfig,
                        ),
                      ) ||
                      t('levels-the-minimum-level', {
                        levelUnit:
                          LevelUnits[
                            getValues('level.unit') as keyof typeof LevelUnits
                          ],
                        minLevelValue: getMinLevelValue(
                          getValues('level.unit'),
                          getFIRValue(),
                          productConfig,
                        ),
                      }),
                    validateLevels: (value): boolean | string =>
                      // level step depends on the unit
                      validateLevels(
                        t,
                        value,
                        getValues('level.unit'),
                        productConfig,
                        getFIRValue(),
                      ),
                  },
                }}
                size="small"
              />
            </Grid>
          </Grid>
        ) : null}

        {/** BETWEEN */}
        <RadioButtonAndLabel
          checked={levelInfoMode === 'BETW' || levelInfoMode === 'BETW_SFC'}
          data-testid="levels-BETW"
          disabled={fieldsDisabled}
          label={t('between')}
          value="BETW"
        />
        {levelInfoMode === 'BETW' || levelInfoMode === 'BETW_SFC' ? (
          <>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              />
              <Grid
                size={{
                  xs: 4,
                  md: 3,
                }}
              >
                <ReactHookFormSelect
                  defaultValue={'FL' as LevelUnits}
                  disabled={fieldsDisabled}
                  isReadOnly={isReadOnly}
                  label={t('unit-label')}
                  name="level.unit"
                  onBeforeChange={(): void => {
                    ensureEditable('level');
                  }}
                  onChange={(): void => {
                    triggerValidations(
                      ['level.value', 'lowerLevel.value', 'lowerLevel.unit'],
                      getValues,
                      trigger,
                    );
                    onChange();
                  }}
                  rules={{
                    validate: {
                      isRequired,
                      validateLevelUnitCombinations: (
                        value,
                      ): boolean | string =>
                        // level step depends on the unit
                        validateLevelUnitCombinations(
                          t,
                          value,
                          levelInfoMode === 'BETW_SFC'
                            ? 'SFC'
                            : getValues('lowerLevel.unit'),
                        ) === true,
                    },
                  }}
                  size="small"
                  sx={styles.unit}
                >
                  {Object.keys(allowedLevelUnitsForFir).map((key) => (
                    <MenuItem key={key} value={key}>
                      {allowedLevelUnitsForFir[key]}
                    </MenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid
                size={{
                  xs: 8,
                  md: 6,
                }}
              >
                <ReactHookFormNumberField
                  autoFocus
                  disabled={fieldsDisabled}
                  isReadOnly={isReadOnly}
                  label={t('levels-upper-level-label')}
                  name="level.value"
                  onBeforeChange={(): void => {
                    ensureEditable('level');
                  }}
                  onChange={(): void => {
                    triggerValidations(
                      ['lowerLevel.value'],
                      getValues,
                      trigger,
                    );
                  }}
                  rules={{
                    validate: {
                      isRequired,
                      isInteger,
                      max: (value): boolean | string =>
                        // The max level depends on the unit
                        isValidMax(
                          value,
                          getMaxLevelValue(
                            getValues('level.unit'),
                            getFIRValue(),
                            productConfig,
                          ),
                        ) ||
                        t('levels-the-maximum-level', {
                          levelUnit:
                            LevelUnits[
                              getValues('level.unit') as keyof typeof LevelUnits
                            ],
                          maxLevelValue: getMaxLevelValue(
                            getValues('level.unit'),
                            getFIRValue(),
                            productConfig,
                          ),
                        }),

                      min: (value): boolean | string =>
                        // The min level depends on the unit
                        isValidMin(
                          value,
                          getMinLevelValue(
                            getValues('level.unit'),
                            getFIRValue(),
                            productConfig,
                          ),
                        ) ||
                        t('levels-the-minimum-level', {
                          levelUnit:
                            LevelUnits[
                              getValues('level.unit') as keyof typeof LevelUnits
                            ],
                          minLevelValue: getMinLevelValue(
                            getValues('level.unit'),
                            getFIRValue(),
                            productConfig,
                          ),
                        }),
                      validateLevels: (value): boolean | string =>
                        // level step depends on the unit
                        validateLevels(
                          t,
                          value,
                          getValues('level.unit'),
                          productConfig,
                          getFIRValue(),
                        ),
                    },
                  }}
                  size="small"
                  sx={styles.levelField}
                  value={level || ''}
                />
              </Grid>
            </Grid>
            <Grid
              alignItems="flex-start"
              container
              justifyItems="flex-end"
              spacing={2}
            >
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={levelInfoMode === 'BETW_SFC'}
                      color="secondary"
                      disabled={fieldsDisabled}
                      name="checkedF"
                      onChange={(): void => {
                        getValues('level.unit') && trigger('level.unit');
                        setValue(
                          'levelInfoMode',
                          getValues('levelInfoMode') === 'BETW'
                            ? 'BETW_SFC'
                            : 'BETW',
                          { shouldDirty: true },
                        );
                      }}
                    />
                  }
                  data-testid="levels-SFC"
                  label="SFC"
                />
              </Grid>
              {levelInfoMode !== 'BETW_SFC' && (
                <>
                  <Grid
                    size={{
                      xs: 4,
                      md: 3,
                    }}
                  >
                    <ReactHookFormSelect
                      defaultValue={
                        getValues('level.unit') || ('FL' as LevelUnits)
                      }
                      disabled={isDisabled}
                      isReadOnly={isReadOnly}
                      label={t('unit-label')}
                      name="lowerLevel.unit"
                      onBeforeChange={(): void => {
                        ensureEditable('lowerLevel');
                      }}
                      onChange={(): void => {
                        triggerValidations(
                          ['lowerLevel.value', 'level.unit'],
                          getValues,
                          trigger,
                        );
                        onChange();
                      }}
                      rules={{
                        validate: {
                          isRequired,
                          validateLevelUnitCombinations: (
                            value,
                          ): boolean | string =>
                            // level step depends on the unit
                            validateLevelUnitCombinations(
                              t,
                              getValues('level.unit'),
                              value,
                            ),
                        },
                      }}
                      size="small"
                      sx={styles.unit}
                    >
                      {Object.keys(allowedLevelUnitsForFir).map((key) => (
                        <MenuItem key={key} value={key}>
                          {allowedLevelUnitsForFir[key]}
                        </MenuItem>
                      ))}
                    </ReactHookFormSelect>
                  </Grid>
                  <Grid
                    size={{
                      xs: 8,
                      md: 6,
                    }}
                  >
                    <ReactHookFormNumberField
                      disabled={isDisabled}
                      isReadOnly={isReadOnly}
                      label={t('levels-lower-level-label')}
                      name="lowerLevel.value"
                      onBeforeChange={(): void => {
                        ensureEditable('lowerLevel');
                      }}
                      rules={{
                        validate: {
                          isRequired,
                          isInteger,
                          max: (value): boolean | string =>
                            // The max level depends on the unit
                            isValidMax(
                              value,
                              getMaxLevelValue(
                                getValues('lowerLevel.unit'),
                                getFIRValue(),
                                productConfig,
                              ),
                            ) ||
                            t('levels-the-maximum-level', {
                              levelUnit:
                                LevelUnits[
                                  getValues(
                                    'lowerLevel.unit',
                                  ) as keyof typeof LevelUnits
                                ],
                              maxLevelValue: getMaxLevelValue(
                                getValues('lowerLevel.unit'),
                                getFIRValue(),
                                productConfig,
                              ),
                            }),
                          min: (value): boolean | string =>
                            // The min level depends on the unit
                            isValidMin(
                              value,
                              getMinLevelValue(
                                getValues('lowerLevel.unit'),
                                getFIRValue(),
                                productConfig,
                              ),
                            ) ||
                            t('levels-the-minimum-level', {
                              levelUnit:
                                LevelUnits[
                                  getValues(
                                    'lowerLevel.unit',
                                  ) as keyof typeof LevelUnits
                                ],
                              minLevelValue: getMinLevelValue(
                                getValues('lowerLevel.unit'),
                                getFIRValue(),
                                productConfig,
                              ),
                            }),
                          isLevelLower: (value): boolean | string =>
                            // The lower level needs to be smaller than upper level
                            isLevelLower(
                              value,
                              getValues('lowerLevel.unit'),
                              getValues('level.value'),
                              getValues('level.unit'),
                            ),

                          validateLevels: (value): boolean | string =>
                            // level step depends on the unit
                            validateLevels(
                              t,
                              value,
                              getValues('lowerLevel.unit'),
                              productConfig,
                              getFIRValue(),
                            ),
                        },
                      }}
                      size="small"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </>
        ) : null}

        {/** ABOVE */}
        <RadioButtonAndLabel
          checked={levelInfoMode === 'ABV' || levelInfoMode === 'TOPS_ABV'}
          data-testid="levels-ABV"
          disabled={fieldsDisabled}
          label={t('above')}
          value="ABV"
        />
        {levelInfoMode === 'ABV' || levelInfoMode === 'TOPS_ABV' ? (
          <Grid
            alignItems="flex-start"
            container
            justifyItems="flex-end"
            spacing={2}
          >
            <Grid
              size={{
                xs: 12,
                md: 3,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={levelInfoMode === 'TOPS_ABV'}
                    disabled={isDisabled || isSigmetTS}
                    onChange={(): void => {
                      if (isSigmetTS) {
                        return;
                      }

                      setValue(
                        'levelInfoMode',
                        levelInfoMode === 'ABV' ? 'TOPS_ABV' : 'ABV',
                        { shouldDirty: true },
                      );
                    }}
                  />
                }
                data-testid="levels-TOPS_ABV"
                label={t('levels-tops-label')}
              />
            </Grid>
            <Grid
              size={{
                xs: 4,
                md: 3,
              }}
            >
              <ReactHookFormSelect
                defaultValue={'FL' as LevelUnits}
                disabled={isDisabled}
                isReadOnly={isReadOnly}
                label={t('unit-label')}
                name="level.unit"
                onBeforeChange={(): void => {
                  ensureEditable('level');
                }}
                onChange={(): void => {
                  triggerValidations(['level.value'], getValues, trigger);
                  onChange();
                }}
                rules={{ validate: { isRequired } }}
                size="small"
                sx={styles.unit}
              >
                {Object.keys(allowedLevelUnitsForFir).map((key) => (
                  <MenuItem key={key} value={key}>
                    {allowedLevelUnitsForFir[key]}
                  </MenuItem>
                ))}
              </ReactHookFormSelect>
            </Grid>
            <Grid
              size={{
                xs: 8,
                md: 6,
              }}
            >
              <ReactHookFormNumberField
                autoFocus
                disabled={isDisabled}
                isReadOnly={isReadOnly}
                label={t('level')}
                name="level.value"
                onBeforeChange={(): void => {
                  ensureEditable('level');
                }}
                rules={{
                  validate: {
                    isRequired,
                    isInteger,
                    max: (value): boolean | string =>
                      // The max level depends on the unit
                      isValidMax(
                        value,
                        getMaxLevelValue(
                          getValues('level.unit'),
                          getFIRValue(),
                          productConfig,
                        ),
                      ) ||
                      t('levels-the-maximum-level', {
                        levelUnit:
                          LevelUnits[
                            getValues('level.unit') as keyof typeof LevelUnits
                          ],
                        maxLevelValue: getMaxLevelValue(
                          getValues('level.unit'),
                          getFIRValue(),
                          productConfig,
                        ),
                      }),
                    min: (value): boolean | string =>
                      // The min level depends on the unit
                      isValidMin(
                        value,
                        getMinLevelValue(
                          getValues('level.unit'),
                          getFIRValue(),
                          productConfig,
                        ),
                      ) ||
                      t('levels-the-minimum-level', {
                        levelUnit:
                          LevelUnits[
                            getValues('level.unit') as keyof typeof LevelUnits
                          ],
                        minLevelValue: getMinLevelValue(
                          getValues('level.unit'),
                          getFIRValue(),
                          productConfig,
                        ),
                      }),
                    validateLevels: (value): boolean | string =>
                      // level step depends on the unit
                      validateLevels(
                        t,
                        value,
                        getValues('level.unit'),
                        productConfig,
                        getFIRValue(),
                      ),
                  },
                }}
                size="small"
              />
            </Grid>
          </Grid>
        ) : null}
      </ReactHookFormRadioGroup>
    </ProductFormFieldLayout>
  );
};

export default Levels;
